// src/utils/rateLimiter.js

/**
 * Simple in-memory rate limiter to prevent abuse of sensitive operations
 * For production, consider using a more robust solution with Redis or similar
 */
class RateLimiter {
    constructor() {
      this.attempts = new Map();
      this.lockouts = new Map();
    }
    
    /**
     * Check if an operation should be rate limited
     * @param {string} key - Unique identifier (e.g., user email or IP)
     * @param {string} operation - Name of the operation
     * @param {Object} options - Rate limiting options
     * @returns {boolean} - True if the operation should be allowed, false if rate limited
     */
    check(key, operation, options = {}) {
      const {
        maxAttempts = 5,
        windowMs = 60 * 1000,  // 1 minute
        lockoutMs = 10 * 60 * 1000 // 10 minutes
      } = options;
      
      const now = Date.now();
      const operationKey = `${key}:${operation}`;
      
      // Check if user is in lockout period
      if (this.lockouts.has(operationKey)) {
        const lockoutEnd = this.lockouts.get(operationKey);
        if (now < lockoutEnd) {
          return false;
        } else {
          this.lockouts.delete(operationKey);
          this.attempts.delete(operationKey);
        }
      }
      
      // Get current attempts
      let attempts = this.attempts.get(operationKey) || { count: 0, resetAt: now + windowMs };
      
      // Reset count if window expired
      if (now > attempts.resetAt) {
        attempts = { count: 0, resetAt: now + windowMs };
      }
      
      // Increment attempt count
      attempts.count += 1;
      this.attempts.set(operationKey, attempts);
      
      // Check if max attempts reached
      if (attempts.count > maxAttempts) {
        this.lockouts.set(operationKey, now + lockoutMs);
        return false;
      }
      
      return true;
    }
    
    /**
     * Reset the rate limit counter for a specific key and operation
     * Useful after successful operations (e.g., successful login)
     */
    reset(key, operation) {
      const operationKey = `${key}:${operation}`;
      this.attempts.delete(operationKey);
      this.lockouts.delete(operationKey);
    }
    
    /**
     * Get the remaining time in the lockout period
     * @returns {number} - Remaining lockout time in milliseconds
     */
    getLockoutTime(key, operation) {
      const operationKey = `${key}:${operation}`;
      if (this.lockouts.has(operationKey)) {
        const now = Date.now();
        const lockoutEnd = this.lockouts.get(operationKey);
        if (now < lockoutEnd) {
          return lockoutEnd - now;
        }
      }
      return 0;
    }
  }
  
  export const authRateLimiter = new RateLimiter();